<template>
  <div class="login-page">
    <!-- Pattern images -->
    <div class="pattern-left">
      <img src="@/assets/img/left-pattern.png" alt="Left Pattern" />
    </div>
    <div class="pattern-right">
      <img src="@/assets/img/right-pattern.png" alt="Right Pattern" />
    </div>

    <div class="container">
      <div class="row justify-content-center align-items-center min-vh-100">
        <div class="col-md-5">
          <div class="card card-custom border-0 shadow-lg">
            <div class="card-body p-5">
              <div class="text-center mb-4">
                <img
                  :src="brandLogo"
                  :alt="brandName"
                  class="mb-3"
                  width="155"
                />
                <h3 class="fw-bold text-title">Sign in to your account</h3>
              </div>

              <!-- Email Input -->
              <div class="mb-3">
                <label for="email" class="form-label text-label"
                  >Email Address</label
                >
                <div class="position-relative">
                  <input
                    class="form-control pe-5 custom-input"
                    type="email"
                    v-model="email"
                    id="email"
                    placeholder="Enter your registered email"
                  />
                </div>
                <div
                  v-if="emailError"
                  class="d-flex align-items-center text-danger small mt-1"
                >
                  <i class="fas fa-exclamation-triangle me-1"></i>
                  <span>{{ emailError }}</span>
                </div>
              </div>

              <!-- Password Input -->
              <div class="mb-3">
                <label for="password" class="form-label text-label"
                  >Password</label
                >
                <div class="position-relative">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control pe-5 custom-input"
                    id="password"
                    v-model="password"
                    placeholder="Enter password"
                  />
                  <div
                    class="position-absolute top-50 end-0 translate-middle-y pe-3"
                    style="cursor: pointer"
                    @click="togglePasswordVisibility('password')"
                  >
                    <svg
                      v-if="!showPassword"
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      class="bi bi-eye text-secondary"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"
                      />
                      <path
                        d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"
                      />
                    </svg>
                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      class="bi bi-eye-slash text-secondary"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"
                      />
                      <path
                        d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"
                      />
                      <path
                        d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  v-if="passwordError"
                  class="d-flex align-items-center text-danger small mt-1"
                >
                  <i class="fas fa-exclamation-triangle me-1"></i>
                  <span>{{ passwordError }}</span>
                </div>
                <div
                  v-if="loginError"
                  class="d-flex align-items-center text-danger small mt-1"
                >
                  <i class="fas fa-exclamation-triangle me-1"></i>
                  <span>{{ loginError }}</span>
                </div>
              </div>

              <div class="d-flex justify-content-end mb-4">
                <a
                  href="/forgot-password"
                  class="text-decoration-none text-success-custom forgot-password-link"
                  >Forgot Password?</a
                >
              </div>

              <argon-button
                @click="submitForm"
                class="btn btn-success-custom btn-lg w-100 custom-button"
                >Continue</argon-button
              >

              <div class="text-center mt-4">
                <p class="mb-0">
                  Don't have an account?
                  <a
                    href="/signup"
                    class="text-success-custom text-decoration-none signup-link"
                    >Create an Account</a
                  >
                </p>
              </div>
            </div>

            <!-- Footer inside card -->
            <div class="card-footer bg-white border-0 text-center py-3">
              <small class="text-muted"
                >© PT Anugrah Multi Remittance 2025 |
                <a href="#" @click.prevent="openTermsModal" class="text-decoration-none text-muted"
                  >Terms & Conditions</a
                >
                |
                <a href="#" @click.prevent="openPrivacyModal" class="text-decoration-none text-muted"
                  >Privacy Policy</a
                ></small
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Terms & Conditions Modal -->
  <div v-if="showTermsModal" class="modal d-block" tabindex="-1" style="background-color: rgba(0, 0, 0, 0.5);" @click.self="showTermsModal = false">
    <div class="modal-dialog modal-xl modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Terms & Conditions</h5>
          <button type="button" class="btn-close" @click="showTermsModal = false" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <h6 class="fw-bold">Domestics Fund Transfer Transaction Terms and Conditions</h6>
          <p>Customers (referring to individual and corporate customers) engage in transactions related to Domestics fund transfers with PT Anugrah Multi Remmitance (hereinafter referred to as "Swiftrans") after agreeing to the contents of these Domestics Fund Transfer Transaction Terms and Conditions (hereinafter referred to as "Terms and Conditions") set by Swiftrans, and the Terms and Conditions constitute a binding contract between the Customer and Swiftrans.</p>
          
          <h6 class="fw-bold mt-4">1. General Terms</h6>
          <p><strong>I. Purpose</strong></p>
          <p>The purpose of these Terms and Conditions is to establish the rights and obligations between Swiftrans and Customers who wish to use the Services provided by Swiftrans and to set the terms for the use of the Services.</p>
          
          <p><strong>II. Definitions</strong></p>
          <p>In these Terms and Conditions, the following terms have the meanings as set forth below:</p>
          <p>1) "Service" refers to the services provided by Swiftrans for transferring funds for Users, both Domesticsly and domestically.</p>
          <p>2) "User" means the customer authorized by Swiftrans to use the Service in accordance with the procedure specified by Swiftrans.</p>
          <p>3) "Correspondent" refers to Swiftrans's partner and/or its business partners located abroad that process fund transfers.</p>
          <p>4) "Internet Fund Transfer" means Domestics fund transfers using the Swiftrans website.</p>
          <p>5) "Login Password" means the code used to access the use of the Service via the Mobile Application or the Internet, created by changing the temporary login password received from Swiftrans.</p>
          <p>6) "Transaction Password" means the code used when conducting transactions using the Swiftrans Service.</p>
          
          <p><strong>III. Use of the Service</strong></p>
          <p>In using the Service, the Customer must complete the registration procedure in accordance with Swiftrans's terms and conditions.</p>
          <p>1) The Customer will be considered to have agreed to these Terms and Conditions.</p>
          <p>2) In principle, the Customer can, in accordance with these Terms and Conditions, use the Service 24 (twenty-four) hours a day, every day throughout the year by downloading and using the Swiftrans Application.</p>
          <p>3) Regardless of the above provisions, Swiftrans may suspend and/or terminate the provision of all or part of the Service for system maintenance or other purposes required for better Service provision.</p>
          <p>4) If Swiftrans suspends the Service temporarily, Swiftrans will provide prior notice to the Customer; however, provided that, if there is an urgent need by Swiftrans due to system failure, etc., Swiftrans may suspend and/or terminate all or part of the system without prior notice.</p>
          
          <h6 class="fw-bold mt-4">2. Registration</h6>
          <p><strong>I. Registration Procedure</strong></p>
          <p>1) When applying to use the Service, the Customer is required to complete the form provided by Swiftrans. The Customer is fully responsible for the accuracy of the data provided in the form and releases Swiftrans from any legal claims that may arise in the future due to errors in the data provided, whether intentional or not.</p>
          <p>2) Swiftrans will conduct a Know Your Customer (KYC) process in accordance with the applicable laws and regulations. Swiftrans may appoint a third party to conduct the KYC procedure.</p>
          <p>3) In the event that a User's account is used by someone else, the User must attach a letter of authorization to the party appointed to conduct the transaction. Swiftrans is not responsible for any transactions carried out by unauthorized parties.</p>
          <p>4) In the event that a Customer does not pass the KYC procedure, Swiftrans has the right to terminate the Customer's registration process, which will result in the Customer being unable to use the Service. Swiftrans's decision is final and cannot be disputed.</p>
          <p>5) Customers are not allowed to:</p>
          <p>a) provide false information;</p>
          <p>b) Use a user's email address with the intention of impersonating that person;</p>
          <p>c) Use another user's email without authorization or permission from the email owner; and</p>
          <p>d) Use or link financial instruments owned by someone other than the Customer into the Swiftrans Application.</p>
          <p>Swiftrans has the right to suspend or terminate a User's account if inaccurate information is found. The decision to suspend and/or terminate an account is at the discretion of Swiftrans and is final and cannot be challenged.</p>
          
          <p><strong>II. Account Upgrade</strong></p>
          <p>Users will be required to upgrade their accounts by providing additional information to fully utilize the features of the Service.</p>
          <p>If a User decides not to upgrade their account, they will not be able to fully enjoy Swiftrans's services.</p>
          
          <p><strong>III. Data Changes</strong></p>
          <p>In the event of changes in a Customer's data, the Customer is required to update the data on the dashboard as provided by Swiftrans.</p>
          <p>Swiftrans will freeze a User's account during the KYC process for the latest User data.</p>
          
          <h6 class="fw-bold mt-4">3. Prohibited Activities and Anti-Money Laundering and Counterterrorism Financing Terms</h6>
          <p>1) Users hereby agree that they will not engage in any of the following when using or accessing the Service:</p>
          <p>a) attempting to access or search the Service or download content from the Service through the use of machinery, software, tools, agents, devices, or mechanisms (including but not limited to robots, crawlers, data mining tools, or the like) other than software and/or agents provided by Swiftrans or generally available on web browsers.</p>
          <p>b) Accessing, modifying, interfering with or using non-public areas of the Service, our computer systems, or the technical delivery systems of our providers, including but not limited to uploading or distributing viruses, adware, spyware, worms, or other harmful code.</p>
          <p>c) Collecting and using information, such as the usernames of other users, real names, email addresses available on the Service, and using them to send unsolicited advertising, junk mail, spam, or other unwanted forms.</p>
          <p>d) using the Service for commercial purposes or for the benefit of third parties or in a manner that contradicts the Terms and Conditions.</p>
          <p>e) violating applicable laws or regulations; or</p>
          <p>f) encouraging or enabling others to do any of the above; or</p>
          <p>g) interfering with the comfort of other users in using the Service, including without limitation, by</p>
          <p>i. making unsolicited offers or advertisements to other users of the Service or</p>
          <p>ii. attempting to collect personal information about other users or third parties without their consent. Swiftrans has the right to investigate and bring to court violations of any of the above, and/or engage and cooperate with law enforcement authorities in prosecuting Users who violate the Terms and Conditions.</p>
          <p>Swiftrans is an entity that enforces anti-money laundering and counterterrorism financing provisions ("APU-PPT"). In providing the Service, Swiftrans may take any actions allowed by law to enforce APU-PPT provisions. By agreeing to these Terms and Conditions, Users consent to Swiftrans taking actions related to APU-PPT enforcement.</p>
          
          <h6 class="fw-bold mt-4">4. Transfer Limits</h6>
          <p>In conducting fund transfers, Swiftrans will apply maximum transfer limits in accordance with the applicable rules.</p>
          
          <h6 class="fw-bold mt-4">5. Changes to the Terms and Conditions</h6>
          <p>Swiftrans has the right to change the Terms and Conditions at any time. Please check the Terms and Conditions regularly to see if there have been any changes.</p>
          <p>In the event of changes to the Terms and Conditions that affect Users' rights and obligations, Swiftrans will make efforts to notify Users of such changes.</p>
          <p>Swiftrans may provide notifications through in-app messages, pop-up messages, or banners within the Service, by sending emails to the address used by Users to register an account, or through similar mechanisms.</p>
          <p>In addition, if changes to the Terms and Conditions alter Users' rights and obligations, Swiftrans may require Users to re-consent to accepting the changed Terms and Conditions.</p>
          <p>If Swiftrans requires Users' consent for changes to the Terms and Conditions, the effects of the changes will only apply after Users accept the changed Terms and Conditions.</p>
          <p>If Users do not accept the changes to the Terms and Conditions, Users can inform Swiftrans to discontinue Users' access to the Service.</p>
          <p>All other changes will apply after the updated Terms and Conditions are published. Disputes arising from the Terms and Conditions will be resolved in accordance with the Terms and Conditions in effect at the time the dispute arises.</p>
          
          <h6 class="fw-bold mt-4">6. Ownership</h6>
          <p>Swiftrans Services (including but not limited to the Application) are protected by copyright, trademarks, and other laws of the Republic of Indonesia.</p>
          <p>Except as expressly provided in the Terms and Conditions, Swiftrans (or its licensors) exclusively owns all rights, ownership, and interests in and to the Services (including but not limited to the Application), including all related intellectual property rights.</p>
          <p>Except as expressly provided in the Terms and Conditions, Customers are not permitted to use the Services, and Swiftrans retains all rights to the Services. Customers may not delete, alter, or obscure any copyright, trademark, service mark, or ownership notices incorporated in or accompanying the Services (including but not limited to the Application).</p>
          <p>Users acknowledge and agree that any feedback, comments, or suggestions provided by Users regarding the Services (including but not limited to the Application) ("Feedback") will be the sole and exclusive property of Swiftrans, and hereby Customers grant rights to all such Feedback.</p>
          
          <h6 class="fw-bold mt-4">7. Disclaimer</h6>
          <p>Customers understand and agree that the Services (including but not limited to the content or material offered through the Service) are provided to Customers on an "as is" and "as available" basis. Without limiting the foregoing, Swiftrans explicitly disclaims all warranties, whether express, implied, or otherwise, including but not limited to the warranties of merchantability, fitness for a particular purpose, non-infringement, and any warranties arising out of the course of dealing or usage of trade.</p>
          <p>Swiftrans makes no warranty that the Service (including but not limited to the content or material obtained from the Service) will meet Customers' needs or be available in an uninterrupted, error-free, or free of harmful components.</p>
          <p>Customers are responsible for the risks associated with the use or access to the Service (including but not limited to the content or material obtained from the Service) and transactions with other service users, and for all damages that may result from such use or access.</p>
          
          <h6 class="fw-bold mt-4">8. Limitation of Liability</h6>
          <p>Swiftrans's liability is limited to the actual value of the loss directly caused by Swiftrans. Swiftrans shall not be liable to the user for any indirect, incidental, special, punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses, resulting from (I) the user's access to or inability to access or use the service (including but not limited to limitations on content obtained from the service); (II) any actions or content of any third party service; or (III) unauthorized access, use or alteration of transmissions or content.</p>
          
          <h6 class="fw-bold mt-4">9. Dispute Resolution and Arbitration</h6>
          <p>Customers and Swiftrans agree that any and all disputes arising in connection with the Terms and Conditions must be resolved through arbitration. Arbitration uses a neutral arbitrator and not a judge or jury, allowing for more limited discovery than in court.</p>
          <p>In most cases, the arbitator can award the same damages as a court. The agreement between Swiftrans and Customers for arbitration of disputes includes, but is not limited to, all claims arising from or relating to any aspect of the Terms and Conditions, whether based in contract, tort, statute, fraud, misrepresentation, or any other legal theory, and regardless of whether the claim arises during or after the Terms and Conditions have terminated.</p>
          <p>Any resolution between Customers and Swiftrans will be governed by the National Arbitration Board of Indonesia ("BANI"), which is recognized by the judicial institutions in Indonesia, and will be conducted in accordance with the rules applicable to BANI.</p>
          <p>Arbitration hearings will be held in Jakarta, and the arbitrator's decision, including decisions on damages, arbitration costs, and legal fees of all parties, as well as other costs, will be final and binding.</p>
          
          <h6 class="fw-bold mt-4">10. Questions and Contact Information</h6>
          <p>This service is provided by PT Anugrah Multi Remmitance, addressed at Komplek Bumi Indah Blok F No 2, Lubuk Baja Kota, Kecamatan Lubuk Baja, Kota Batam. Please contact Swiftrans if the User has any questions regarding these Terms and Conditions. The User can contact Swiftrans by sending a letter to the above address or by sending an email to cs@swiftrans.id.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showTermsModal = false">Close</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Privacy Policy Modal -->
  <div v-if="showPrivacyModal" class="modal d-block" tabindex="-1" style="background-color: rgba(0, 0, 0, 0.5);" @click.self="showPrivacyModal = false">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Privacy Policy</h5>
          <button type="button" class="btn-close" @click="showPrivacyModal = false" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p class="fw-bold mb-3">Last Updated: July 21, 2025</p>
          
          <h6 class="fw-bold mt-4">1. Introduction</h6>
          <p>PT Anugrah Multi Remittance, hereinafter referred to as "Swiftrans", respects and is fully committed to protecting the privacy of our Users. This Privacy Policy explains how we collect, store, use, disclose, and protect personal data obtained through the use of Swiftrans' international remittance services.</p>
          <p>By using Swiftrans' services, the User is deemed to have read, understood, and agreed to the terms of this Privacy Policy.</p>
          
          <h6 class="fw-bold mt-4">2. Personal Data Collected</h6>
          <p>Swiftrans may collect the following types of information:</p>
          <p><strong>Identity Information:</strong> Full name, identification number (ID/Passport), date of birth, nationality.</p>
          <p><strong>Contact Information:</strong> Address, phone number, and email address.</p>
          <p><strong>Transaction Information:</strong> Amount and destination of the transfer, recipient's name, bank account details.</p>
          <p><strong>Financial Information:</strong> Proof of source of funds, bank account used.</p>
          <p><strong>Technical Information:</strong> IP address, activity logs, device and application data.</p>
          <p><strong>Other Information</strong> required in accordance with laws and regulations on Anti-Money Laundering (AML) and Counter-Terrorism Financing (CTF).</p>
          
          <h6 class="fw-bold mt-4">3. Purpose of Data Collection</h6>
          <p>The personal data collected will be used for the following purposes:</p>
          <p>• To provide, manage, and improve Swiftrans' services.</p>
          <p>• To carry out identity verification and Know Your Customer (KYC) processes.</p>
          <p>• To comply with applicable laws and regulations (including AML-CTF).</p>
          <p>• To detect and prevent fraud, misuse, or other illegal activities.</p>
          <p>• To provide information to competent authorities when legally required.</p>
          <p>• To communicate important updates regarding transactions or services.</p>
          
          <h6 class="fw-bold mt-4">4. Legal Basis for Data Processing</h6>
          <p>We process personal data based on the following legal grounds:</p>
          <p>• The User's consent.</p>
          <p>• Legal obligations (including regulations from Bank Indonesia, PPATK, and other applicable laws).</p>
          <p>• Legitimate interests of the company in ensuring security and operational efficiency.</p>
          <p>• Contractual obligations between Swiftrans and the User.</p>
          
          <h6 class="fw-bold mt-4">5. Data Storage and Protection</h6>
          <p>• Data is stored on servers protected both physically and digitally, in accordance with industry standards.</p>
          <p>• Swiftrans implements encryption, firewalls, and access controls to prevent unauthorized access.</p>
          <p>• Data will be retained as long as necessary for processing purposes or as permitted by law.</p>
          
          <h6 class="fw-bold mt-4">6. Sharing of Personal Data</h6>
          <p>Swiftrans may share personal data with the following third parties:</p>
          <p>• Overseas correspondent partners for international remittance purposes.</p>
          <p>• Domestic correspondent partners for domestic fund transfers.</p>
          <p>• Verification and KYC service providers.</p>
          <p>• Regulatory and legal authorities (Bank Indonesia, PPATK, Police, etc.).</p>
          <p>All third parties are required to adhere to data protection principles equivalent to our own.</p>
          
          <h6 class="fw-bold mt-4">7. User Rights Regarding Personal Data</h6>
          <p>Users have the right to:</p>
          <p>• Access and obtain a copy of their personal data.</p>
          <p>• Update or correct inaccurate personal data.</p>
          <p>• Withdraw consent (with certain consequences on the services).</p>
          <p>• Request deletion of personal data, in accordance with applicable laws and regulations.</p>
          <p>Requests regarding these rights may be submitted via: <strong>privacy@swiftrans.co.id</strong></p>
          
          <h6 class="fw-bold mt-4">8. Cookies and Similar Technologies</h6>
          <p>We use cookies and similar technologies to:</p>
          <p>• Enhance user experience.</p>
          <p>• Collect data on service usage.</p>
          <p>• Provide more relevant content.</p>
          <p>Users can manage their cookie preferences through their respective browser settings.</p>
          
          <h6 class="fw-bold mt-4">9. Changes to the Privacy Policy</h6>
          <p>Swiftrans may update this Privacy Policy from time to time. We will notify Users of any material changes via the application, email, or other official communication channels.</p>
          <p>Continued use of the services following any changes will constitute acceptance of the updated Privacy Policy.</p>
          
          <h6 class="fw-bold mt-4">10. Contact Information</h6>
          <p>For any questions, information requests, or complaints regarding this Privacy Policy, please contact us at:</p>
          <p><strong>PT Anugrah Multi Remittance (Swiftrans)</strong></p>
          <p><strong>Office Address:</strong> Komplek Bumi Indah Blok F No. 2, Lubuk Baja Kota, Lubuk Baja District, Batam City</p>
          <p><strong>Email:</strong> cs@swiftrans.co.id</p>
          
          <h6 class="fw-bold mt-4">11. Governing Law</h6>
          <p>This Policy shall be governed by and construed in accordance with the laws of the Republic of Indonesia, including Law Number 27 of 2022 on Personal Data Protection, and its implementing regulations.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showPrivacyModal = false">Close</button>
        </div>
      </div>
    </div>
  </div>

  <Loading :isLoading="isLoading" />
</template>

<script>
import { onBeforeUnmount, onBeforeMount } from "vue";
import { useStore } from "vuex";
import ArgonButton from "@/components/ArgonButton";
import { buildFormData } from "../../utils/param-builder";
import { loginMerchant } from "../../api/auth";
import Loading from "@/components/Loading";
import { useBranding } from "@/utils/branding";

export default {
  components: {
    ArgonButton,
    Loading,
  },
  data() {
    const { brandLogo, brandName } = useBranding();
    return {
      email: "",
      password: "",
      showPassword: false,
      isLoading: false,
      loginError: "",
      emailError: "",
      passwordError: "",
      brandLogo,
      brandName,
      showTermsModal: false,
      showPrivacyModal: false,
    };
  },
  methods: {
    openTermsModal() {
      this.showTermsModal = true;
    },
    openPrivacyModal() {
      this.showPrivacyModal = true;
    },
    async submitForm() {
      // Clear previous errors
      this.emailError = "";
      this.passwordError = "";
      this.loginError = "";

      if (!this.validate()) {
        return;
      }

      this.isLoading = true;
      const body = {
        email: this.email,
        password: this.password,
      };

      try {
        const formData = buildFormData(body);
        const response = await loginMerchant(formData);
        if (response.status === "success") {
          this.$store.commit("auth/setEmail", response.data.merchant_email);
          localStorage.setItem("merchant_email", response.data.merchant_email);
          this.$router.push({ name: "VerifyOtp" });
        } else if (response.token) {
          await this.$store.dispatch("auth/setUserToken", response.data.token);
          this.$router.push("/home");
        } else {
          this.loginError = response.message || "Registration failed";
        }
      } catch (error) {
        this.loginError =
          error.response?.data.message || "Invalid email or password";
      } finally {
        this.isLoading = false;
      }
    },
    togglePasswordVisibility(field) {
      if (field === "password") {
        this.showPassword = !this.showPassword;
      }
    },
    validate() {
      let isValid = true;

      if (!this.email) {
        this.emailError = "Email is required";
        isValid = false;
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.email)) {
          this.emailError = "Please enter a valid email address";
          isValid = false;
        }
      }

      if (!this.password) {
        this.passwordError = "Password is required";
        isValid = false;
      }

      return isValid;
    },
    logout() {
      this.$store.dispatch("auth/clearToken");
      localStorage.removeItem("auth_token");
      this.$router.push({ name: "Signin" });
    },
  },
  setup() {
    const store = useStore();
    const body = document.getElementsByTagName("body")[0];

    onBeforeMount(() => {
      const storedEmail = localStorage.getItem("merchant_email");
      const storedToken = localStorage.getItem("auth_token");
      if (storedEmail) {
        store.commit("auth/setEmail", storedEmail);
      }
      if (storedToken) {
        store.commit("auth/setToken", storedToken);
      }
      store.state.hideConfigButton = true;
      store.state.showNavbar = false;
      store.state.showSidenav = false;
      store.state.showFooter = false;
      body.classList.remove("bg-gray-100");
    });

    onBeforeUnmount(() => {
      store.state.hideConfigButton = false;
      store.state.showNavbar = true;
      store.state.showSidenav = true;
      store.state.showFooter = true;
      body.classList.add("bg-gray-100");
    });
  },
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  background: linear-gradient(
    180deg,
    #ffffff 0%,
    #ffffff 65%,
    #e8f5e8 65%,
    #d4f1d4 100%
  );
  position: relative;
  overflow: hidden;
}

.login-page::before {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40vh;
  background: linear-gradient(135deg, #e8f5e8 0%, #d4f1d4 50%, #c8f0c8 100%);
  z-index: 0;
}

/* Pattern Images */
.pattern-left {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}

.pattern-left img {
  width: auto;
  height: 450px;
  object-fit: contain;
}

.pattern-right {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
}

.pattern-right img {
  width: auto;
  height: 450px;
  object-fit: contain;
}

.container {
  position: relative;
  z-index: 1;
}

.card-custom {
  border-radius: 1rem;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  z-index: 2;
}

.shadow-lg {
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.text-label {
  font-family: "IBM Plex Sans Devanagari";
  font-weight: 500;
  font-size: 14px;
  color: #374151;
  margin-bottom: 0.5rem;
}

.text-title {
  font-family: "IBM Plex Sans Devanagari";
  font-weight: 600;
  font-size: 32px;
  line-height: 40px;
  color: #1f2937;
  text-align: center;
}

.custom-input {
  padding: 0.875rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  transition: all 0.3s ease;
  font-size: 16px;
  background: #ffffff;
}

.custom-input:focus {
  border-color: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
  outline: none;
}

.text-success-custom {
  color: #16a34a !important;
  font-weight: 600;
}

.forgot-password-link {
  font-size: 14px;
  transition: all 0.3s ease;
}

.forgot-password-link:hover {
  color: #15803d !important;
  text-decoration: underline !important;
}

.signup-link {
  font-weight: 600;
  transition: all 0.3s ease;
}

.signup-link:hover {
  color: #15803d !important;
  text-decoration: underline !important;
}

.custom-button {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border: none;
  border-radius: 0.75rem;
  padding: 0.875rem 2rem;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 14px 0 rgba(34, 197, 94, 0.3);
}

.custom-button:hover {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px 0 rgba(34, 197, 94, 0.4);
}

.custom-button:active {
  transform: translateY(0);
}

.card-footer {
  border-radius: 0 0 1rem 1rem;
}

/* Animation for card entrance */
.card-custom {
  animation: slideUp 0.8s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .login-page::before {
    height: 30vh;
  }

  .pattern-left img,
  .pattern-right img {
    height: 400px;
  }

  .text-title {
    font-size: 24px;
    line-height: 32px;
  }

  .card-body {
    padding: 2rem !important;
  }
}

@media (max-width: 576px) {
  .pattern-left img,
  .pattern-right img {
    height: 150px;
  }
}

/* Override Bootstrap success color globally for this component */
.btn-success-custom {
  background-color: #22c55e;
  border-color: #22c55e;
}

.btn-success-custom:hover {
  background-color: #16a34a;
  border-color: #16a34a;
}

.btn-success-custom:focus,
.btn-success-custom.focus {
  box-shadow: 0 0 0 0.2rem rgba(34, 197, 94, 0.25);
}

.btn-success-custom:disabled,
.btn-success-custom.disabled {
  background-color: #22c55e;
  border-color: #22c55e;
  opacity: 0.8;
}
</style>
